import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent } from 'react';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bell, Bookmark, FolderOpen, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Drawer } from 'vaul';
import type { ApiWatchlist, ChartSymbol, WatchlistFlagColor } from '../types/api';
import type { WatchlistQuote, WatchlistSort, WatchlistViewItem } from './AppShell.types';

const SWIPE_ACTION_WIDTH = 172;
const SWIPE_SINGLE_ACTION_WIDTH = 86;
const SWIPE_OPEN_THRESHOLD = 42;
const SWIPE_INTENT_THRESHOLD = 8;
const SWIPE_RESISTANCE = 0.35;
const SECTION_LONG_PRESS_MS = 900;
const watchlistFlagColors: WatchlistFlagColor[] = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'];

export function WatchlistView({ watchlists, activeWatchlistId, items, selectedSymbol, sort, loading, quotes, draggingItemId, dragOverItemId, draggingWatchlistId, dragOverWatchlistId, openSwipeItemId, error, canManageWatchlists, canUseAlarms, onWatchlistChange, onCreateWatchlist, onRenameWatchlist, onDeleteWatchlist, onSelectSymbol, onRemoveSymbol, onCreateSectionAbove, onRemoveItem, onRenameSection, onAlarmSymbol, onSetItemFlag, onReorderStart, onReorderPreview, onReorderEnd, onReorderCancel, onWatchlistReorderStart, onWatchlistReorderPreview, onWatchlistReorderEnd, onWatchlistReorderCancel, onOpenSwipe, onCloseSwipe }: {
  watchlists: ApiWatchlist[];
  activeWatchlistId: string | null;
  items: WatchlistViewItem[];
  selectedSymbol: string;
  sort: WatchlistSort;
  loading: boolean;
  quotes: Record<string, WatchlistQuote | undefined>;
  draggingItemId: string | null;
  dragOverItemId: string | null;
  draggingWatchlistId: string | null;
  dragOverWatchlistId: string | null;
  openSwipeItemId: string | null;
  error: string | null;
  canManageWatchlists: boolean;
  canUseAlarms: boolean;
  onWatchlistChange(id: string): void;
  onCreateWatchlist(): void;
  onRenameWatchlist(watchlist: ApiWatchlist): void;
  onDeleteWatchlist(watchlist: ApiWatchlist): void;
  onSelectSymbol(symbol: ChartSymbol): void;
  onRemoveSymbol(symbol: ChartSymbol): void;
  onCreateSectionAbove(item: WatchlistViewItem): void;
  onRemoveItem(item: WatchlistViewItem): void;
  onRenameSection(item: WatchlistViewItem): void;
  onAlarmSymbol(symbol: ChartSymbol): void;
  onSetItemFlag(item: WatchlistViewItem, flagColor: WatchlistFlagColor | null): void;
  onReorderStart(itemId: string, currentOrder: WatchlistViewItem[]): void;
  onReorderPreview(activeItemId: string, targetItemId: string): void;
  onReorderEnd(): void;
  onReorderCancel(): void;
  onWatchlistReorderStart(watchlistId: string): void;
  onWatchlistReorderPreview(activeWatchlistId: string, targetWatchlistId: string): void;
  onWatchlistReorderEnd(): void;
  onWatchlistReorderCancel(): void;
  onOpenSwipe(itemId: string): void;
  onCloseSwipe(): void;
}) {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 8 },
    }),
  );
  const tabSensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 8 },
    }),
  );
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const symbolListRef = useRef<HTMLDivElement | null>(null);
  const restoredSymbolRef = useRef<string | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const sectionLongPressTriggeredRef = useRef(false);
  const suppressTabClickRef = useRef(false);
  const [tabIndicator, setTabIndicator] = useState({ left: 0, width: 0 });
  const [actionWatchlist, setActionWatchlist] = useState<ApiWatchlist | null>(null);
  const [actionSymbolItem, setActionSymbolItem] = useState<WatchlistViewItem | null>(null);
  const activeWatchlist = watchlists.find((watchlist) => watchlist.id === activeWatchlistId) ?? null;
  const watchlistOrderKey = watchlists.map((watchlist) => watchlist.id).join('|');
  const displayedItems = sortWatchlistItems(items, sort, quotes);
  const activeDragItem = draggingItemId ? displayedItems.find((item) => item.id === draggingItemId) : null;
  const canReorder = !actionSymbolItem;
  const showSkeleton = loading && displayedItems.length === 0;

  useEffect(() => {
    if (!draggingItemId) return;

    const preventTouchScroll = (event: TouchEvent) => event.preventDefault();
    window.addEventListener('touchmove', preventTouchScroll, { passive: false });
    return () => window.removeEventListener('touchmove', preventTouchScroll);
  }, [draggingItemId]);

  useLayoutEffect(() => {
    if (!activeWatchlistId) {
      setTabIndicator({ left: 0, width: 0 });
      return;
    }
    const activeTab = tabRefs.current[activeWatchlistId];
    if (!activeTab) return;
    setTabIndicator({ left: activeTab.offsetLeft, width: activeTab.offsetWidth });
  }, [activeWatchlistId, watchlistOrderKey]);

  useLayoutEffect(() => {
    if (loading || !selectedSymbol) return;
    const restoreKey = `${activeWatchlistId ?? ''}|${sort}|${selectedSymbol}`;
    if (restoredSymbolRef.current === restoreKey) return;

    const list = symbolListRef.current;
    if (!list) return;
    const selectedRow = Array.from(list.querySelectorAll<HTMLElement>('[data-symbol-row]'))
      .find((row) => row.dataset.symbolRow === selectedSymbol);
    if (!selectedRow) return;

    restoredSymbolRef.current = restoreKey;
    list.scrollTop = Math.max(0, selectedRow.offsetTop - (list.clientHeight - selectedRow.clientHeight) / 2);
  }, [activeWatchlistId, displayedItems.length, loading, selectedSymbol, sort]);

  function clearLongPressTimer() {
    if (!longPressTimerRef.current) return;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  }

  function startWatchlistLongPress(event: PointerEvent<HTMLButtonElement>, watchlist: ApiWatchlist) {
    if (!canManageWatchlists) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    clearLongPressTimer();
    longPressStartRef.current = { x: event.clientX, y: event.clientY };
    suppressTabClickRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      navigator.vibrate?.(10);
      suppressTabClickRef.current = true;
      setActionWatchlist(watchlist);
    }, 550);
  }

  function moveWatchlistLongPress(event: PointerEvent<HTMLButtonElement>) {
    const start = longPressStartRef.current;
    if (!start) return;
    if (Math.abs(event.clientX - start.x) > 8 || Math.abs(event.clientY - start.y) > 8) clearLongPressTimer();
  }

  function endWatchlistLongPress() {
    clearLongPressTimer();
    longPressStartRef.current = null;
  }

  return (
    <div
      className="watchlist-view"
      onPointerDownCapture={(event) => {
        if (!openSwipeItemId) return;
        if ((event.target as HTMLElement).closest('.swipe-open')) return;
        onCloseSwipe();
      }}
      onFocusCapture={(event) => {
        if (!openSwipeItemId) return;
        if ((event.target as HTMLElement).closest('.swipe-open')) return;
        onCloseSwipe();
      }}
    >
      {canManageWatchlists && <div className={draggingWatchlistId ? 'watchlist-tabs watchlist-tabs-dragging' : 'watchlist-tabs'} aria-label="Watchlists">
        <span className="watchlist-tab-indicator" style={{ transform: `translateX(${tabIndicator.left}px)`, width: tabIndicator.width }} aria-hidden="true" />
        {showSkeleton ? <WatchlistTabSkeleton /> : (
          <DndContext
            sensors={tabSensors}
            collisionDetection={closestCenter}
            onDragStart={(event: DragStartEvent) => {
              navigator.vibrate?.(10);
              clearLongPressTimer();
              suppressTabClickRef.current = true;
              onWatchlistReorderStart(String(event.active.id));
            }}
            onDragOver={(event: DragOverEvent) => {
              if (event.over) onWatchlistReorderPreview(String(event.active.id), String(event.over.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              if (!event.over) onWatchlistReorderCancel();
              else onWatchlistReorderEnd();
            }}
            onDragCancel={onWatchlistReorderCancel}
          >
            <SortableContext items={watchlists.map((watchlist) => watchlist.id)} strategy={horizontalListSortingStrategy}>
              {watchlists.map((watchlist) => (
                <SortableWatchlistTab
                  key={watchlist.id}
                  watchlist={watchlist}
                  active={activeWatchlistId === watchlist.id}
                  dragging={draggingWatchlistId === watchlist.id}
                  dragOver={Boolean(draggingWatchlistId && dragOverWatchlistId === watchlist.id && draggingWatchlistId !== watchlist.id)}
                  disabled={watchlists.length < 2}
                  setTabNode={(node) => {
                    tabRefs.current[watchlist.id] = node;
                  }}
                  onClick={() => {
                    if (suppressTabClickRef.current) {
                      suppressTabClickRef.current = false;
                      return;
                    }
                    onWatchlistChange(watchlist.id);
                  }}
                  onDoubleClick={() => {
                    if (canManageWatchlists) onRenameWatchlist(watchlist);
                  }}
                  onContextMenu={(event) => {
                    if (!canManageWatchlists) return;
                    event.preventDefault();
                    setActionWatchlist(watchlist);
                  }}
                  onPointerDown={(event) => startWatchlistLongPress(event, watchlist)}
                  onPointerMove={moveWatchlistLongPress}
                  onPointerUp={endWatchlistLongPress}
                  onPointerCancel={endWatchlistLongPress}
                  onPointerLeave={endWatchlistLongPress}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
        {showSkeleton || !canManageWatchlists ? null : <button type="button" className="watchlist-tab watchlist-tab-add" onClick={onCreateWatchlist} aria-label="Create watchlist">+</button>}
      </div>}

      {canManageWatchlists && <div className="watchlist-toolbar">
        <div className="watchlist-section-title">
          {showSkeleton ? <span className="skeleton-block skeleton-watchlist-title" aria-hidden="true" /> : activeWatchlist?.name ?? 'Watchlist'}
        </div>
      </div>}
      {error && <div className="watchlist-error">{error}</div>}
      <DndContext
        sensors={canReorder ? sensors : []}
        collisionDetection={closestCenter}
        autoScroll
        onDragStart={(event: DragStartEvent) => {
          navigator.vibrate?.(10);
          if (!canReorder) return;
          onReorderStart(String(event.active.id), displayedItems);
        }}
        onDragOver={(event: DragOverEvent) => {
          if (canReorder && event.over) onReorderPreview(String(event.active.id), String(event.over.id));
        }}
        onDragEnd={(event: DragEndEvent) => {
          if (sectionLongPressTriggeredRef.current) {
            sectionLongPressTriggeredRef.current = false;
            onReorderCancel();
            return;
          }
          if (!canReorder || !event.over) onReorderCancel();
          else onReorderEnd();
        }}
        onDragCancel={onReorderCancel}
      >
        <SortableContext items={displayedItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
          <div ref={symbolListRef} className={draggingItemId ? 'symbol-list symbol-list-dragging' : 'symbol-list'}>
            {showSkeleton ? <WatchlistSkeletonRows /> : displayedItems.length > 0 ? displayedItems.map((item) => item.item_type === 'section' ? (
              <SortableSectionRow
                key={item.id}
                item={item}
                dragging={draggingItemId === item.id}
                dragOver={Boolean(draggingItemId && dragOverItemId === item.id && draggingItemId !== item.id)}
                canReorder={canReorder}
                swipeOpen={openSwipeItemId === item.id}
                onRename={onRenameSection}
                onRemove={onRemoveItem}
                onCreateSectionAbove={(targetItem) => {
                  sectionLongPressTriggeredRef.current = true;
                  onReorderCancel();
                  onCreateSectionAbove(targetItem);
                }}
                onOpenSwipe={() => onOpenSwipe(item.id)}
                onCloseSwipe={onCloseSwipe}
              />
            ) : item.symbolData ? (
              <SortableSymbolRow
                key={item.id}
                itemId={item.id}
                item={item}
                inactive={Boolean(item.isInactive)}
                flagColor={item.flag_color ?? null}
                symbol={item.symbolData}
                selected={item.symbolData.name === selectedSymbol}
                quote={item.isInactive ? undefined : quotes[item.symbolData.name]}
                dragging={draggingItemId === item.id}
                dragOver={Boolean(draggingItemId && dragOverItemId === item.id && draggingItemId !== item.id)}
                swipeOpen={openSwipeItemId === item.id}
                canReorder={canReorder}
                onSelect={onSelectSymbol}
                onRemove={onRemoveSymbol}
                onCreateSectionAbove={() => {
                  sectionLongPressTriggeredRef.current = true;
                  onReorderCancel();
                  onCreateSectionAbove(item);
                }}
                onOpenActions={() => {
                  sectionLongPressTriggeredRef.current = true;
                  onReorderCancel();
                  onCloseSwipe();
                  setActionSymbolItem(item);
                }}
                onAlarm={onAlarmSymbol}
                canUseAlarms={canUseAlarms}
                onOpenSwipe={() => onOpenSwipe(item.id)}
                onCloseSwipe={onCloseSwipe}
              />
            ) : null) : (
              <div className="empty-state">No symbols yet. Tap + to add one.</div>
            )}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeDragItem?.item_type === 'section' ? <DragOverlaySectionRow item={activeDragItem} /> : activeDragItem?.symbolData ? <DragOverlaySymbolRow symbol={activeDragItem.symbolData} quote={quotes[activeDragItem.symbolData.name]} /> : null}
        </DragOverlay>
      </DndContext>
      <Drawer.Root open={Boolean(actionWatchlist)} onOpenChange={(open) => {
        if (!open) setActionWatchlist(null);
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content watchlist-tab-actions-sheet" aria-describedby={undefined}>
            <div className="sheet-handle" aria-hidden="true" />
            <div className="sheet-header">
              <Drawer.Title asChild><strong>{actionWatchlist?.name ?? 'Watchlist'}</strong></Drawer.Title>
              <Drawer.Close asChild>
                <button type="button" className="icon-button" aria-label="Close watchlist actions"><X size={24} strokeWidth={1.9} /></button>
              </Drawer.Close>
            </div>
            <button type="button" className="sheet-action-button" onClick={() => {
              if (actionWatchlist) onRenameWatchlist(actionWatchlist);
              setActionWatchlist(null);
            }}>Rename watchlist</button>
            <button type="button" className="sheet-action-button sheet-action-button-danger" onClick={() => {
              if (actionWatchlist) onDeleteWatchlist(actionWatchlist);
              setActionWatchlist(null);
            }} disabled={watchlists.length <= 1}>Delete watchlist</button>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
      <Drawer.Root open={Boolean(actionSymbolItem)} onOpenChange={(open) => {
        if (!open) setActionSymbolItem(null);
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="drawer-overlay" />
          <Drawer.Content className="drawer-content watchlist-symbol-actions-sheet" aria-describedby={undefined}>
            <div className="sheet-handle" aria-hidden="true" />
            {actionSymbolItem?.symbolData ? (
              <div className="symbol-action-preview">
                <span className={actionSymbolItem.isInactive ? 'symbol-main symbol-main-inactive' : 'symbol-main'}>
                  <strong>{actionSymbolItem.symbolData.name}</strong>
                  <span>{actionSymbolItem.symbolData.description}</span>
                </span>
                <QuoteBlock symbol={actionSymbolItem.symbolData} quote={actionSymbolItem.isInactive ? undefined : quotes[actionSymbolItem.symbolData.name]} inactive={Boolean(actionSymbolItem.isInactive)} />
              </div>
            ) : null}
            <div className="symbol-action-group">
              <div className="symbol-action-row symbol-action-row-static">
                <span>Flag</span>
                <div className="symbol-action-flag-list" aria-label="Flag colors">
                  {watchlistFlagColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`symbol-action-flag-button flag-${color}`}
                      aria-label={`Set ${color} flag`}
                      onClick={() => {
                        if (actionSymbolItem) onSetItemFlag(actionSymbolItem, color);
                        setActionSymbolItem(null);
                      }}
                    >
                      <Bookmark size={18} strokeWidth={2.1} fill="currentColor" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
              <button type="button" className="symbol-action-row" disabled={!actionSymbolItem?.flag_color} onClick={() => {
                if (actionSymbolItem) onSetItemFlag(actionSymbolItem, null);
                setActionSymbolItem(null);
              }}>
                <span>Remove flag</span>
                <Bookmark className={actionSymbolItem?.flag_color ? `flag-${actionSymbolItem.flag_color}` : undefined} size={22} strokeWidth={2.1} fill="currentColor" aria-hidden="true" />
              </button>
              <button type="button" className="symbol-action-row" onClick={() => {
                if (actionSymbolItem) onCreateSectionAbove(actionSymbolItem);
                setActionSymbolItem(null);
              }}>
                <span>Add section above</span>
                <Plus size={22} strokeWidth={2.1} aria-hidden="true" />
              </button>
              {canUseAlarms && (
                <button type="button" className="symbol-action-row" onClick={() => {
                  if (actionSymbolItem?.symbolData) onAlarmSymbol(actionSymbolItem.symbolData);
                  setActionSymbolItem(null);
                }}>
                  <span>Add alert</span>
                  <Bell size={22} strokeWidth={2.1} aria-hidden="true" />
                </button>
              )}
              <button type="button" className="symbol-action-row" onClick={() => {
                if (actionSymbolItem?.symbolData) onSelectSymbol(actionSymbolItem.symbolData);
                setActionSymbolItem(null);
              }}>
                <span>Open chart</span>
                <FolderOpen size={22} strokeWidth={2.1} aria-hidden="true" />
              </button>
              <button type="button" className="symbol-action-row symbol-action-row-danger" onClick={() => {
                if (actionSymbolItem?.symbolData) onRemoveSymbol(actionSymbolItem.symbolData);
                setActionSymbolItem(null);
              }}>
                <span>Remove</span>
                <Trash2 size={22} strokeWidth={2.1} aria-hidden="true" />
              </button>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}

function WatchlistTabSkeleton() {
  return (
    <span className="watchlist-tab-skeleton" aria-hidden="true">
      <span className="skeleton-block skeleton-watchlist-tab" />
    </span>
  );
}

function SortableWatchlistTab({ watchlist, active, dragging, dragOver, disabled, setTabNode, onClick, onDoubleClick, onContextMenu, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave }: {
  watchlist: ApiWatchlist;
  active: boolean;
  dragging: boolean;
  dragOver: boolean;
  disabled: boolean;
  setTabNode(node: HTMLButtonElement | null): void;
  onClick(): void;
  onDoubleClick(): void;
  onContextMenu(event: MouseEvent<HTMLButtonElement>): void;
  onPointerDown(event: PointerEvent<HTMLButtonElement>): void;
  onPointerMove(event: PointerEvent<HTMLButtonElement>): void;
  onPointerUp(): void;
  onPointerCancel(): void;
  onPointerLeave(): void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: watchlist.id, disabled });

  return (
    <button
      ref={(node) => {
        setNodeRef(node);
        setTabNode(node);
      }}
      type="button"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        'watchlist-tab',
        active ? 'active' : '',
        dragging || isDragging ? 'dragging' : '',
        dragOver ? 'drag-over' : '',
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerLeave}
      {...attributes}
      {...listeners}
    >
      {watchlist.name}
    </button>
  );
}

function WatchlistSkeletonRows() {
  return Array.from({ length: 6 }, (_, index) => (
    <div key={index} className="symbol-row skeleton-row" aria-hidden="true">
      <div className="symbol-row-content">
        <span className="symbol-main skeleton-main">
          <span className="skeleton-block skeleton-symbol" />
          <span className="skeleton-block skeleton-description" />
        </span>
        <span className="symbol-quote skeleton-quote">
          <span className="skeleton-block skeleton-price" />
          <span className="skeleton-block skeleton-change" />
        </span>
      </div>
    </div>
  ));
}

function DragOverlaySymbolRow({ symbol, quote }: { symbol: ChartSymbol; quote: WatchlistQuote | undefined }) {
  return (
    <div className="symbol-row drag-overlay-row">
      <div className="symbol-row-content">
        <span className="symbol-main">
          <strong>{symbol.name}</strong>
          <span>{symbol.description}</span>
        </span>
        <QuoteBlock symbol={symbol} quote={quote} inactive={false} />
      </div>
    </div>
  );
}

function DragOverlaySectionRow({ item }: { item: WatchlistViewItem }) {
  return (
    <div className="symbol-row section-row drag-overlay-row">
      <div className="section-row-content">
        <strong>{item.name}</strong>
      </div>
    </div>
  );
}

function SortableSectionRow({ item, dragging, dragOver, canReorder, swipeOpen, onRename, onRemove, onCreateSectionAbove, onOpenSwipe, onCloseSwipe }: {
  item: WatchlistViewItem;
  dragging: boolean;
  dragOver: boolean;
  canReorder: boolean;
  swipeOpen: boolean;
  onRename(item: WatchlistViewItem): void;
  onRemove(item: WatchlistViewItem): void;
  onCreateSectionAbove(item: WatchlistViewItem): void;
  onOpenSwipe(): void;
  onCloseSwipe(): void;
}) {
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled: !canReorder || swipeOpen });

  return (
    <SwipeableSectionRow
      item={item}
      dragging={dragging || isDragging}
      dragOver={dragOver}
      canReorder={canReorder}
      swipeOpen={swipeOpen}
      setNodeRef={setNodeRef}
      setDragHandleRef={setActivatorNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
      dndStyle={{ transform: CSS.Transform.toString(transform), transition }}
      onRename={onRename}
      onRemove={onRemove}
      onCreateSectionAbove={onCreateSectionAbove}
      onOpenSwipe={onOpenSwipe}
      onCloseSwipe={onCloseSwipe}
    />
  );
}

function SwipeableSectionRow({ item, dragging, dragOver, canReorder, swipeOpen, setNodeRef, setDragHandleRef, dragAttributes, dragListeners, dndStyle, onRename, onRemove, onCreateSectionAbove, onOpenSwipe, onCloseSwipe }: {
  item: WatchlistViewItem;
  dragging: boolean;
  dragOver: boolean;
  canReorder: boolean;
  swipeOpen: boolean;
  setNodeRef(node: HTMLElement | null): void;
  setDragHandleRef(node: HTMLElement | null): void;
  dragAttributes: ReturnType<typeof useSortable>['attributes'];
  dragListeners: ReturnType<typeof useSortable>['listeners'];
  dndStyle: CSSProperties;
  onRename(item: WatchlistViewItem): void;
  onRemove(item: WatchlistViewItem): void;
  onCreateSectionAbove(item: WatchlistViewItem): void;
  onOpenSwipe(): void;
  onCloseSwipe(): void;
}) {
  const [offset, setOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const gestureRef = useRef<{ startX: number; startY: number; initialOffset: number; mode: 'horizontal' | 'vertical' | null } | null>(null);
  const rowLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const offsetRef = useRef(0);
  const swipeReveal = Math.max(0, Math.min(SWIPE_ACTION_WIDTH, -offset));
  const swipeProgress = swipeReveal / SWIPE_ACTION_WIDTH;
  const swipeActionStyle = { opacity: swipeProgress } satisfies CSSProperties;
  const swipeActionIconStyle = { transform: `translateX(${(1 - swipeProgress) * 10}px)` } satisfies CSSProperties;

  function updateOffset(nextOffset: number) {
    offsetRef.current = nextOffset;
    setOffset(nextOffset);
  }

  function clearRowLongPressTimer() {
    if (!rowLongPressTimerRef.current) return;
    clearTimeout(rowLongPressTimerRef.current);
    rowLongPressTimerRef.current = null;
  }

  useEffect(() => {
    if (!swiping) updateOffset(swipeOpen ? -SWIPE_ACTION_WIDTH : 0);
  }, [swipeOpen, swiping]);

  useEffect(() => {
    if (!dragging) return;
    clearRowLongPressTimer();
    gestureRef.current = null;
  }, [dragging]);

  useEffect(() => () => clearRowLongPressTimer(), []);

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest('.swipe-row-action')) return;
    if (target.closest('.symbol-row-drag-handle')) return;
    const row = event.currentTarget;
    const { pointerId } = event;

    row.setPointerCapture(event.pointerId);
    gestureRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      initialOffset: swipeOpen ? -SWIPE_ACTION_WIDTH : 0,
      mode: null,
    };
    clearRowLongPressTimer();
    rowLongPressTimerRef.current = setTimeout(() => {
      navigator.vibrate?.(12);
      if (row.hasPointerCapture(pointerId)) row.releasePointerCapture(pointerId);
      gestureRef.current = null;
      onCreateSectionAbove(item);
    }, SECTION_LONG_PRESS_MS);
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    const gesture = gestureRef.current;
    if (!gesture) return;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (!gesture.mode && Math.max(Math.abs(deltaX), Math.abs(deltaY)) > SWIPE_INTENT_THRESHOLD) {
      gesture.mode = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      clearRowLongPressTimer();
    }
    if (gesture.mode !== 'horizontal') return;

    event.preventDefault();
    setSwiping(true);
    const rawOffset = gesture.initialOffset + deltaX;
    const nextOffset = rawOffset < -SWIPE_ACTION_WIDTH
      ? -SWIPE_ACTION_WIDTH + (rawOffset + SWIPE_ACTION_WIDTH) * SWIPE_RESISTANCE
      : Math.min(0, rawOffset);
    updateOffset(nextOffset);
  }

  function handlePointerUp() {
    clearRowLongPressTimer();
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture || gesture.mode !== 'horizontal') return;

    setSwiping(false);
    if (offsetRef.current <= -SWIPE_OPEN_THRESHOLD) {
      updateOffset(-SWIPE_ACTION_WIDTH);
      onOpenSwipe();
    } else {
      updateOffset(0);
      onCloseSwipe();
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      className={[
        'symbol-row',
        'section-row',
        dragging ? 'dragging' : '',
        dragOver ? 'drag-over' : '',
        swiping ? 'swiping' : '',
        swipeOpen ? 'swipe-open' : '',
        canReorder ? 'reorderable' : '',
      ].filter(Boolean).join(' ')}
    >
      <button type="button" className="swipe-row-action swipe-rename-action" style={swipeActionStyle} tabIndex={swipeOpen ? 0 : -1} onClick={() => onRename(item)}>
        <Pencil size={24} strokeWidth={2.1} aria-hidden="true" style={swipeActionIconStyle} />
        <span className="sr-only">Rename</span>
      </button>
      <button type="button" className="swipe-row-action swipe-delete-action" style={swipeActionStyle} tabIndex={swipeOpen ? 0 : -1} onClick={() => onRemove(item)}>
        <Trash2 size={24} strokeWidth={2.1} aria-hidden="true" style={swipeActionIconStyle} />
        <span className="sr-only">Delete</span>
      </button>
      <div
        className="section-row-content"
        style={{ transform: `translate3d(${offset}px, 0, 0)` }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onContextMenu={(event) => event.preventDefault()}
        onPointerDown={handlePointerDown}
      >
        {canReorder && (
          <span ref={setDragHandleRef} className="symbol-row-drag-handle" aria-label="Drag to reorder" {...dragAttributes} {...dragListeners} />
        )}
        <strong>{item.name}</strong>
      </div>
    </div>
  );
}

function SortableSymbolRow(props: {
  itemId: string;
  item: WatchlistViewItem;
  inactive: boolean;
  flagColor: WatchlistFlagColor | null;
  symbol: ChartSymbol;
  selected: boolean;
  quote: WatchlistQuote | undefined;
  canReorder: boolean;
  canUseAlarms: boolean;
  dragging: boolean;
  dragOver: boolean;
  swipeOpen: boolean;
  onSelect(symbol: ChartSymbol): void;
  onRemove(symbol: ChartSymbol): void;
  onCreateSectionAbove(): void;
  onOpenActions(): void;
  onAlarm(symbol: ChartSymbol): void;
  onOpenSwipe(): void;
  onCloseSwipe(): void;
}) {
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.itemId,
    disabled: !props.canReorder || props.swipeOpen,
  });

  return (
    <SwipeableSymbolRow
      {...props}
      dragging={props.dragging || isDragging}
      setNodeRef={setNodeRef}
      setDragHandleRef={setActivatorNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
      dndStyle={{ transform: CSS.Transform.toString(transform), transition }}
    />
  );
}

function SwipeableSymbolRow({ inactive, flagColor, symbol, selected, quote, canReorder, canUseAlarms, dragging, dragOver, swipeOpen, setNodeRef, setDragHandleRef, dragAttributes, dragListeners, dndStyle, onSelect, onRemove, onCreateSectionAbove: _onCreateSectionAbove, onOpenActions, onAlarm, onOpenSwipe, onCloseSwipe }: {
  inactive: boolean;
  flagColor: WatchlistFlagColor | null;
  symbol: ChartSymbol;
  selected: boolean;
  quote: WatchlistQuote | undefined;
  canReorder: boolean;
  canUseAlarms: boolean;
  dragging: boolean;
  dragOver: boolean;
  swipeOpen: boolean;
  setNodeRef(node: HTMLElement | null): void;
  setDragHandleRef(node: HTMLElement | null): void;
  dragAttributes: ReturnType<typeof useSortable>['attributes'];
  dragListeners: ReturnType<typeof useSortable>['listeners'];
  dndStyle: CSSProperties;
  onSelect(symbol: ChartSymbol): void;
  onRemove(symbol: ChartSymbol): void;
  onCreateSectionAbove(): void;
  onOpenActions(): void;
  onAlarm(symbol: ChartSymbol): void;
  onOpenSwipe(): void;
  onCloseSwipe(): void;
}) {
  const [offset, setOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const gestureRef = useRef<{ startX: number; startY: number; initialOffset: number; mode: 'horizontal' | 'vertical' | null } | null>(null);
  const didSwipeRef = useRef(false);
  const didLongPressRef = useRef(false);
  const rowLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const offsetRef = useRef(0);
  const swipeActionWidth = canUseAlarms ? SWIPE_ACTION_WIDTH : SWIPE_SINGLE_ACTION_WIDTH;
  const swipeReveal = Math.max(0, Math.min(swipeActionWidth, -offset));
  const swipeProgress = swipeReveal / swipeActionWidth;
  const swipeActionStyle = { opacity: swipeProgress } satisfies CSSProperties;
  const swipeActionIconStyle = { transform: `translateX(${(1 - swipeProgress) * 10}px)` } satisfies CSSProperties;

  function updateOffset(nextOffset: number) {
    offsetRef.current = nextOffset;
    setOffset(nextOffset);
  }

  function clearRowLongPressTimer() {
    if (!rowLongPressTimerRef.current) return;
    clearTimeout(rowLongPressTimerRef.current);
    rowLongPressTimerRef.current = null;
  }

  useEffect(() => {
    if (!swiping) updateOffset(swipeOpen ? -swipeActionWidth : 0);
  }, [swipeActionWidth, swipeOpen, swiping]);

  useEffect(() => {
    if (!dragging) return;
    clearRowLongPressTimer();
    gestureRef.current = null;
  }, [dragging]);

  useEffect(() => () => clearRowLongPressTimer(), []);

  function handlePointerDown(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest('.swipe-row-action')) return;
    if (target.closest('.symbol-row-drag-handle')) return;
    const row = event.currentTarget;
    const { pointerId } = event;

    row.setPointerCapture(event.pointerId);

    gestureRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      initialOffset: swipeOpen ? -swipeActionWidth : 0,
      mode: null,
    };
    didSwipeRef.current = false;
    didLongPressRef.current = false;
    clearRowLongPressTimer();
    rowLongPressTimerRef.current = setTimeout(() => {
      navigator.vibrate?.(12);
      if (row.hasPointerCapture(pointerId)) row.releasePointerCapture(pointerId);
      didLongPressRef.current = true;
      gestureRef.current = null;
      onOpenActions();
    }, SECTION_LONG_PRESS_MS);
  }

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    const gesture = gestureRef.current;
    if (!gesture) return;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    if (!gesture.mode && Math.max(Math.abs(deltaX), Math.abs(deltaY)) > SWIPE_INTENT_THRESHOLD) {
      gesture.mode = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      clearRowLongPressTimer();
    }
    if (gesture.mode !== 'horizontal') return;

    event.preventDefault();
    setSwiping(true);
    const rawOffset = gesture.initialOffset + deltaX;
    const nextOffset = rawOffset < -swipeActionWidth
      ? -swipeActionWidth + (rawOffset + swipeActionWidth) * SWIPE_RESISTANCE
      : Math.min(0, rawOffset);
    updateOffset(nextOffset);
  }

  function handlePointerUp() {
    clearRowLongPressTimer();
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture || gesture.mode !== 'horizontal') return;

    didSwipeRef.current = true;
    setSwiping(false);
    if (offsetRef.current <= -SWIPE_OPEN_THRESHOLD) {
      updateOffset(-swipeActionWidth);
      onOpenSwipe();
    } else {
      updateOffset(0);
      onCloseSwipe();
    }
  }

  return (
    <div
      ref={setNodeRef}
      data-symbol-row={symbol.name}
      style={dndStyle}
      className={[
        'symbol-row',
        selected ? 'selected' : '',
        dragging ? 'dragging' : '',
        dragOver ? 'drag-over' : '',
        swiping ? 'swiping' : '',
        swipeOpen ? 'swipe-open' : '',
        canReorder ? 'reorderable' : '',
      ].filter(Boolean).join(' ')}
    >
      {canUseAlarms && (
        <button type="button" className="swipe-row-action swipe-alarm-action" style={swipeActionStyle} tabIndex={swipeOpen ? 0 : -1} onClick={() => onAlarm(symbol)}>
          <Bell size={24} strokeWidth={2.1} aria-hidden="true" style={swipeActionIconStyle} />
          <span className="sr-only">Set alarm</span>
        </button>
      )}
      <button type="button" className="swipe-row-action swipe-delete-action" style={swipeActionStyle} tabIndex={swipeOpen ? 0 : -1} onClick={() => onRemove(symbol)}>
        <Trash2 size={24} strokeWidth={2.1} aria-hidden="true" style={swipeActionIconStyle} />
        <span className="sr-only">Delete</span>
      </button>
      <div
        className="symbol-row-content"
        style={{ transform: `translate3d(${offset}px, 0, 0)` }}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onContextMenu={(event) => event.preventDefault()}
        onPointerDown={handlePointerDown}
      >
        {canReorder && (
          <span ref={setDragHandleRef} className="symbol-row-drag-handle" aria-label="Drag to reorder" {...dragAttributes} {...dragListeners} />
        )}
        <button
          type="button"
          className="symbol-select-button"
          onClick={() => {
            if (didLongPressRef.current) {
              didLongPressRef.current = false;
              return;
            }
            if (didSwipeRef.current) {
              didSwipeRef.current = false;
              return;
            }
            if (swipeOpen) {
              onCloseSwipe();
              return;
            }
            onSelect(symbol);
          }}
        >
          {flagColor ? <Bookmark className={`symbol-row-flag flag-${flagColor}`} size={14} strokeWidth={2.2} fill="currentColor" aria-hidden="true" /> : <span className="symbol-row-flag-placeholder" aria-hidden="true" />}
          <span className={inactive ? 'symbol-main symbol-main-inactive' : 'symbol-main'}>
            <strong>{symbol.name}</strong>
            <span>{symbol.description}</span>
          </span>
          <QuoteBlock symbol={symbol} quote={quote} inactive={inactive} />
        </button>
      </div>
    </div>
  );
}

function QuoteBlock({ symbol, quote, inactive }: { symbol: ChartSymbol; quote: WatchlistQuote | undefined; inactive: boolean }) {
  if (inactive) {
    return <span className="symbol-quote symbol-quote-inactive">Inactive</span>;
  }
  if (!quote) {
    return (
      <span className="symbol-quote skeleton-quote" aria-label="Loading quote">
        <span className="skeleton-block skeleton-price" />
        <span className="skeleton-block skeleton-change" />
      </span>
    );
  }

  const direction = quote.change >= 0 ? 'positive' : 'negative';
  const sign = quote.change > 0 ? '+' : '';
  const decimals = decimalsFromPricescale(symbol.pricescale);
  const changeText = `${sign}${formatChange(quote.change, decimals)} ${formatPercent(quote.percent)}`;
  return (
    <span className={`symbol-quote ${direction}`}>
      <strong>{formatPrice(quote.price, decimals)}</strong>
      <span key={changeText} className="quote-change">{changeText}</span>
    </span>
  );
}

function formatPrice(value: number, decimals?: number): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimals ?? (value >= 100 ? 2 : 4),
    minimumFractionDigits: decimals ?? (value >= 1 ? 2 : 0),
  }).format(value);
}

function formatChange(value: number, decimals?: number): string {
  return Math.abs(value).toFixed(decimals ?? (Math.abs(value) >= 1 ? 2 : 4));
}

function formatPercent(value: number): string {
  if (value === 0) return '0%';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function decimalsFromPricescale(pricescale: number): number {
  if (!Number.isFinite(pricescale) || pricescale <= 1) return 0;
  return Math.min(8, Math.max(0, Math.round(Math.log10(pricescale))));
}

function sortWatchlistItems(items: WatchlistViewItem[], sort: WatchlistSort, quotes: Record<string, WatchlistQuote | undefined>): WatchlistViewItem[] {
  if (sort === 'manual') return items;

  const activeSort = sort;
  const sortedItems: WatchlistViewItem[] = [];
  let sectionSymbols: WatchlistViewItem[] = [];

  function flushSectionSymbols() {
    if (sectionSymbols.length === 0) return;
    sortedItems.push(...sectionSymbols.sort((a, b) => compareSymbols(a, b, activeSort, quotes)));
    sectionSymbols = [];
  }

  for (const item of items) {
    if (item.item_type === 'section') {
      flushSectionSymbols();
      sortedItems.push(item);
    } else if (item.symbolData) {
      sectionSymbols.push(item);
    } else {
      flushSectionSymbols();
      sortedItems.push(item);
    }
  }

  flushSectionSymbols();
  return sortedItems;
}

function compareSymbols(a: WatchlistViewItem, b: WatchlistViewItem, sort: Exclude<WatchlistSort, 'manual'>, quotes: Record<string, WatchlistQuote | undefined>): number {
  const symbolA = a.symbolData;
  const symbolB = b.symbolData;
  if (!symbolA || !symbolB) return 0;
  if (sort === 'flag') return compareFlaggedItems(a, b) || symbolA.name.localeCompare(symbolB.name);
  if (sort === 'symbol') return symbolA.name.localeCompare(symbolB.name);

  const quoteA = quotes[symbolA.name];
  const quoteB = quotes[symbolB.name];
  if (!quoteA && !quoteB) return symbolA.name.localeCompare(symbolB.name);
  if (!quoteA) return 1;
  if (!quoteB) return -1;

  if (sort === 'gainers') return quoteB.percent - quoteA.percent || symbolA.name.localeCompare(symbolB.name);
  if (sort === 'losers') return quoteA.percent - quoteB.percent || symbolA.name.localeCompare(symbolB.name);
  return quoteB.price - quoteA.price || symbolA.name.localeCompare(symbolB.name);
}

function compareFlaggedItems(a: WatchlistViewItem, b: WatchlistViewItem): number {
  return flagColorRank(a.flag_color) - flagColorRank(b.flag_color);
}

function flagColorRank(color: WatchlistFlagColor | null | undefined): number {
  if (!color) return 999;
  const index = watchlistFlagColors.indexOf(color);
  return index >= 0 ? index : 999;
}
